import {
    bindFirst,
    createTSlatePlugin,
    type Element,
    type OmitFirst,
    type PluginConfig,
} from '@udecode/plate';

import { isAbove } from './api/isAbove';
import { isBelow } from './api/isBelow';
import { changeFigureKind } from './transforms/changeFigureKind';
import { deleteFigureCaption } from './transforms/deleteFigureCaption';
import { insertFigureCaption } from './transforms/insertFigureCaption';
import type { FigureKind, TFigureCaptionElement } from './types';

export type FigureCaptionConfig = PluginConfig<
    'figure-caption',
    {
        captionPosition: Record<FigureKind, 'above' | 'below'>;
    },
    {
        figureCaption: {
            isAbove: OmitFirst<typeof isAbove>,
            isBelow: OmitFirst<typeof isBelow>
        }
    },
    {
        insert: {
            figureCaption: (at: Element, figureKind: FigureKind, position: 'above' | 'below') => void
        },
        remove: {
            figureCaption: OmitFirst<typeof deleteFigureCaption>
        },
        figureCaption: {
            setKind: OmitFirst<typeof changeFigureKind>
        }
    }
>;

/** Enables support for hyperlinks. */
export const BaseFigureCaptionPlugin = createTSlatePlugin<FigureCaptionConfig>({
    key: 'figure-caption',
    node: {
        isElement: true,
        isInline: false
    },
    options: {
        captionPosition: {
            image: 'below',
            table: 'below',
            chart: 'below',
            formula: 'below',
            map: 'below',
            other: 'below',
        },
    }
})
    .extendEditorApi<FigureCaptionConfig['api']>(({ editor }) => ({
        figureCaption: {
            isAbove: bindFirst(isAbove, editor),
            isBelow: bindFirst(isBelow, editor),
        },
    }))
    .extendEditorTransforms<FigureCaptionConfig['transforms']>(({ editor }) => ({
        insert: {
            figureCaption: (at: Element, figureKind: FigureKind, position: 'above' | 'below') => {
                return insertFigureCaption(editor, at, figureKind, position);
            },
        },
        remove: {
            figureCaption: bindFirst(deleteFigureCaption, editor),
        },
        figureCaption: {
            setKind: bindFirst(changeFigureKind, editor),
        }
    }))
    .overrideEditor(({
        editor,
        tf: { normalizeNode },
    }) => {
        return {
            transforms: {
                normalizeNode: ([node, path]) => {
                    if (node.type === 'figure-caption') {
                        const typedNode = node as TFigureCaptionElement;

                        if (!typedNode.align) {
                            editor.tf.setNodes({
                                ...node,
                                align: 'left',
                            }, { at: path });
                        }

                        if (!typedNode.figureKind) {
                            editor.tf.setNodes({
                                ...node,
                                figureKind: 'image',
                            }, { at: path });
                        }
                    }

                    normalizeNode([node, path]);
                }
            }
        };
    });
